import type { GatewayDiagnosis, HouseholdDiagnosis } from "@/lib/api";

/**
 * Pure helpers for reporting a USR connector giving up (floor-heating and
 * ventilation's connecting stages), pulled out of the screens so the copy
 * they produce is unit-testable without a component-testing stack. See
 * `recordIssue` in `@/lib/api`.
 */

/** Pick this connector's own reading out of the household-wide diagnosis. */
export function pickGatewayDiagnosis(
  diagnosis: HouseholdDiagnosis | undefined,
  kind: GatewayDiagnosis["kind"],
): GatewayDiagnosis | undefined {
  return diagnosis?.gateways?.find((gateway) => gateway.kind === kind);
}

/**
 * Verdicts that mean "we could not tell", as opposed to "something is wrong".
 * They must never block a household: an unreadable entity registry is missing
 * evidence, and dead-ending a house over it would be worse than the fault we
 * were looking for.
 */
const INCONCLUSIVE_VERDICTS = new Set(["undiagnosed"]);

/**
 * Does this diagnosis say the connector is not delivering data?
 *
 * Keyed on `action` rather than on a list of verdicts, because the backend
 * guarantees every verdict maps onto one of the five actions and that
 * `none` means nothing is wrong (see ACTION_NONE in network_diagnosis.py).
 * A new verdict therefore classifies itself here instead of silently landing
 * on the wrong side of a list nobody remembered to update.
 *
 * Fails open, deliberately. No diagnosis, or an inconclusive one, is not a
 * fault: only a positive verdict with something to do about it blocks the
 * checklist. A diagnosis outage must not stop every onboarding in the field.
 */
export function isLinkFault(diagnosis: GatewayDiagnosis | undefined): boolean {
  if (!diagnosis || INCONCLUSIVE_VERDICTS.has(diagnosis.verdict)) {
    return false;
  }
  return diagnosis.action !== "none";
}

/**
 * The verdict a connecting stage should put on screen right now, if any.
 *
 * A fault (`isLinkFault`) minus the one verdict that already has a screen of
 * its own: `mac_mismatch` sends the module to its own stage, which has a photo
 * and a question the checklist layout cannot hold. Without this exclusion the
 * notice would paint for the frame between that verdict arriving and the
 * effect moving the stage on.
 */
export function pickNoticeDiagnosis(
  diagnosis: GatewayDiagnosis | undefined,
): GatewayDiagnosis | undefined {
  if (!isLinkFault(diagnosis) || diagnosis?.verdict === "mac_mismatch") {
    return undefined;
  }
  return diagnosis;
}

/**
 * Can somebody standing next to the hardware carry this fix out?
 *
 * Reads the backend's own signals rather than a verdict list, so a verdict
 * added later sorts itself: `needs_someone_on_site` is exactly the set of
 * actions nobody can carry out remotely (ON_SITE_ACTIONS in
 * network_diagnosis.py), and `escalate` is the action that says the fix is
 * ours to make.
 *
 * `action` wins when the two disagree. A verdict that reads "we will sort this
 * out" while sending a resident to the cabinet contradicts itself, and the one
 * that changes side (Wi-Fi `not_found`, once the app stopped pretending a
 * bridge reconfiguration was something a household could be walked through)
 * changes its action first.
 *
 * A question does not make a fault the resident's. The escalating `not_found`
 * verdict still asks whether a USR-W610 network is visible, because that
 * answer is evidence VELUX needs, not an instruction.
 */
export function canBeFixedOnSite(diagnosis: GatewayDiagnosis): boolean {
  return diagnosis.action !== "escalate" && diagnosis.needs_someone_on_site === true;
}

/**
 * Is this fault out of the household's hands?
 *
 * Anything without a notice to show is not an escalation, which keeps the
 * caller from hiding its timed skip over a connector that is merely slow.
 * Shares `canBeFixedOnSite` with `DiagnosisNotice`, so the screen cannot hide
 * its skip on the strength of one rule while the notice renders the other.
 */
export function isEscalation(diagnosis: GatewayDiagnosis | undefined): boolean {
  const notice = pickNoticeDiagnosis(diagnosis);
  return notice != null && !canBeFixedOnSite(notice);
}

const NUMBER_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
  "twenty",
];

/** Spell out small counts per the copy rules; digits stand in beyond that. */
function spellOut(count: number): string {
  return NUMBER_WORDS[count] ?? String(count);
}

/**
 * A short factual note on what was tried before a connector gave up, for the
 * admin report `recordIssue` feeds. `attempts` counts every provision call
 * made (the first tap plus every automatic retry), so `attempts - 1` is the
 * automatic-retry count.
 */
export function describeWhatWasTried(
  attempts: number,
  diagnosis: GatewayDiagnosis | undefined,
): string {
  const retries = Math.max(attempts - 1, 0);
  const tried =
    retries === 0
      ? "One try to register the gateway."
      : `One try and ${spellOut(retries)} automatic ${retries === 1 ? "retry" : "retries"} to register the gateway.`;
  if (!diagnosis) {
    return `${tried} The background diagnosis had not returned a verdict yet.`;
  }
  return `${tried} The diagnosis found: ${diagnosis.detail}`;
}

/**
 * What the household said when asked whether the label on their connector
 * matches the MAC the hub found, for the `what_was_tried` the report reads.
 *
 * The two answers mean opposite things and neither is auto-corrected: "yes"
 * means our record is wrong, "no" means the hub is set up for a device that
 * is not in the house. Recording which one was given is what lets support
 * tell the two apart later.
 */
export function describeMacMismatchAnswer(matches: boolean): string {
  return matches
    ? "The household said the label matches the MAC the hub found, so our record is wrong."
    : "The household said the label does not match the MAC the hub found, so the hub is set up for a device that is not in their house.";
}
